import { createContext, useCallback, useEffect, useState, useRef, useContext } from "react";
import config from "../config/config";
import Loading from "../components/loading";
import { userSession } from "../observers/userObserver";

export const UserSessionContext = createContext();

export const UserSessionProvider = ({ children }) => {
  const [user, setUser] = useState({
    userName: "",
    userEmail: "",
    userType: "USER",
    userMLNOFlag: false,
  });
  const [accessToken, setAccessToken] = useState(localStorage.getItem("token"));
  const [refreshToken, setRefreshToken] = useState(
    localStorage.getItem("refreshToken")
  );
  const [sessionReady, setSessionReady] = useState(false);
  const refreshTimeoutRef = useRef(null);

  const clearRefreshSchedule = () => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  };

  const scheduleRefreshToken = (expInSeconds) => {
    clearRefreshSchedule();
    const currentTime = Math.floor(Date.now() / 1000);
    const delay = (expInSeconds - currentTime - 29 * 60) * 1000; // Refresh 15 minute before expiry
    console.log("Scheduling token refresh in", delay / 1000, "seconds");

    if (delay > 0) {
      refreshTimeoutRef.current = setTimeout(() => {
        refreshAccessToken();
      }, delay);
    } else {
      refreshAccessToken(); // Token is near expiry, refresh immediately
    }
  };


  
  // const scheduleRefreshToken = () => {
  //   clearRefreshSchedule();
  
  //   const delay = 10 * 24 * 60 * 60 * 1000; // 10 days in milliseconds
  //   console.log("📅 Scheduling token refresh in", delay / (60 * 60 * 1000), "hours");
  
  //   refreshTimeoutRef.current = setTimeout(() => {
  //     refreshAccessToken();
  //   }, delay);
  // };
  
  const setData = (user, token, exp, refreshToken) => {
    console.log("Setting user session data:", user)
    
    setUser({
      userName: user.username,
      userEmail: user.userEmail,
      userType: user.userType,
      userMLNOFlag: user.userMLNOFlag,
      userAISearchEnabled: user.userAISearchEnabled,
    });
    if (token) localStorage.setItem("token", token);
    if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
    if (token) setAccessToken(token);
    if (refreshToken) setRefreshToken(refreshToken);
    setSessionReady(true);

    userSession.setData(
      {
        userName: user.username,
        userEmail: user.userEmail,
        userType: user.userType,
        userMLNOFlag: user.userMLNOFlag,
        userAISearchEnabled: user.userAISearchEnabled,
      }, token, exp ? Number(exp) * 1000 : null, refreshToken
    )

    if (exp) scheduleRefreshToken(exp);
  };

  const logout = (reason="") => {
    if (reason) sessionStorage.setItem("logoutReason", "You have been logged out. " + reason);
    localStorage.clear();
    setUser({
      userName: "",
      userEmail: "",
      userType: "USER",
      userMLNOFlag: false,
    });
    setAccessToken(null);
    setRefreshToken(null);
    setSessionReady(false);
    clearRefreshSchedule();
    userSession.clear();
    window.location.href = "/login";
  };

  const refreshAccessToken = async () => {
    const storedRefreshToken = localStorage.getItem("refreshToken");
    if (!storedRefreshToken) {
      // logout("Session Timeout, No Refresh Token Exists. Please Login Again.");
      return false;
    }

    try {
      console.log("Refreshing access token...");
      const response = await fetch(`${config["backend-url"]}/refresh_token`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${storedRefreshToken}`,
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        const data = await response.json();
        setData(
          {
            userId: data.user.userId,
            userEmail: data.user.userEmail,
            username: data.user.username,
            userType: data.user.UserType,
            userMLNOFlag: data.user.User_MLNOFlag,
            userAISearchEnabled: data.user.AI_Search_Enabled ?? data.user.AISearchEnabled ?? false,
          },
          data.token,
          data.exp,
          data.refreshToken
        );
        return true;
      } else {
        setSessionReady(false);
        console.log("Error refreshing access token:", response.status, response.statusText);
        logout("Error in refreshing access token: " + response.statusText + "(" + response.status + ")");
        return false;
      }
    } catch (error) {
      console.error("Error refreshing access token:", error);
      setSessionReady(false);
      logout("Error in refreshing access token: " + error.message);
      return false;
    }
  };

  const validateToken = async () => {

    if (!localStorage.getItem("token")) return false;

      try {
        const response = await fetch(`${config["backend-url"]}/validate_token`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        });
        if (response.ok) {
          const data = await response.json();
          setUser({
            userName: data.user.username,
            userEmail: data.user.userEmail,
            userType: data.user.UserType,
            userMLNOFlag: data.user.User_MLNOFlag,
            userAISearchEnabled: data.user.AI_Search_Enabled ?? data.user.AISearchEnabled ?? false,
          });
          // /validate_token doesn't return exp — parse it from the JWT token directly
          let tokenExp = data.exp;
          if (!tokenExp) {
            try {
              const token = localStorage.getItem("token");
              tokenExp = JSON.parse(atob(token.split('.')[1])).exp;
            } catch (e) {
              console.warn("Could not parse token expiry:", e);
            }
          }
          userSession.setData(
            {
              userName: data.user.username,
              userEmail: data.user.userEmail,
              userType: data.user.UserType,
              userMLNOFlag: data.user.User_MLNOFlag,
              userAISearchEnabled: data.user.AI_Search_Enabled ?? data.user.AISearchEnabled ?? false,
            },
            getToken(),
            tokenExp ? Number(tokenExp) * 1000 : null,
            getRefreshToken()
          )
          if (tokenExp) scheduleRefreshToken(tokenExp);
          return true;
        }
        return false;
    } catch (error) {
      console.warn("Token validation error:", error);
      return false;
    }
  }

  const validateAndSetUser = async () => {
    if (await validateToken()) return true;
    // return await refreshAccessToken(); // TODO: Uncomment this line to enable refresh on validation failure
    return false;
  };

  const getAllTokensFromURL = async () => {
    setSessionReady(false);
    const urlParams = new URLSearchParams(window.location.search);
    let token = urlParams.get("token");

    // adding fallback token
    let fallbackToken = token;

    // Clean token from URL

    if (token) {
      
    } else {
      token = localStorage.getItem("token");
    }

    if (!token) return false;

    try {
      let response = await fetch(`${config["backend-url"]}/get_all_token`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      let ok = response.ok // to simulate failure for testing fallback token
      let status = response.status;
      let statusText = response.statusText;
      if (!ok) {
        console.error("Error using URL token:", statusText);
        // do not logout the user if fallback token is present
        if (fallbackToken) {
          console.log("Using fallback token, not logging out.");
          setData({}, fallbackToken, null, null);
          return true;
          // TODO: setTimeout to getAllTokensFromURL again after 5 seconds untill fixed.
        }
        logout("Error in using URL token: " + statusText + "(" + status + ")");
        return false;
      }

      const data = await response.json();

      setData(
        {
          userId: data.user.userId,
          userEmail: data.user.userEmail,
          username: data.user.username,
          userType: data.user.UserType,
          userMLNOFlag: data.user.User_MLNOFlag,
          userAISearchEnabled: data.user.AI_Search_Enabled ?? data.user.AISearchEnabled ?? false,
        },
        data.token,
        data.exp,
        data.refreshToken
      );

      return true;
    } catch (error) {
      console.error("Error using URL token:", error);
      logout("Error in using URL token: " + error.message);
      return false;
    }
  };

  // Utility Getters
  const getUserID = useCallback(() => user?.userId || null, [user]);
  const getUserEmail = useCallback(() => user?.userEmail || null, [user]);
  const getUserName = useCallback(() => user?.username || null, [user]);
  const getUserType = useCallback(() => user?.UserType || null, [user]);
  const isUserMLNO = useCallback(() => user?.User_MLNOFlag || null, [user]);
  const getToken = useCallback(() => localStorage.getItem("token"), []);
  const getRefreshToken = useCallback(() => localStorage.getItem("refreshToken"), []);
  const getUser = useCallback(() => user, [user]);

  useEffect(() => {
    userSession.setRefreshHandler(refreshAccessToken);
    const init = async () => {
      const tokenSetFromURL = await getAllTokensFromURL();
      if (!tokenSetFromURL) {
        console.log("No token set from URL");
        if (accessToken) await validateAndSetUser();
        else if (refreshToken) await refreshAccessToken();
        else window.location.href = "/login"; // No tokens — redirect to login silently
      } else {
        console.log("Token set from URL", accessToken);
        if (localStorage.getItem("token")) await validateAndSetUser();
      }
    };
    init();

    return () => clearRefreshSchedule(); // Cleanup on unmount
  }, []);

  return (
    <UserSessionContext.Provider
      value={{
        user,
        accessToken,
        refreshToken,
        sessionReady,
        logout,
        getUserID,
        getUserEmail,
        getUserName,
        getUserType,
        isUserMLNO,
        getToken,
        getRefreshToken,
        getUser,
      }}
    >
      {sessionReady ? children : <Loading />}
    </UserSessionContext.Provider>
  );
};

export const useUserSession = () => useContext(UserSessionContext);